import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { createDepartment } from '@/modules/Employee/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { DEPARTMENT_STATUS } from '@/modules/Employee/constants'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'

export interface DepartmentCreateModalProps extends ModalProps {
    refetch: () => void
}

interface FormValues {
    department_name: string
    department_code: string
    department_description: string
    department_status: string
    department_leader_id: number | null
}

const DepartmentCreateModal: React.FC<DepartmentCreateModalProps> = ({
    open,
    onClose,
    refetch,
}) => {
    const { t } = useTranslation()

    const { control, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: {
            department_name: '',
            department_code: '',
            department_description: '',
            department_status: 'active',
            department_leader_id: null,
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createDepartment },
        {
            reactQueryKeys: QUERY_KEYS.DEPARTMENTS,
            successMessage: 'department_created_success',
            errorMessage: 'error_occurred',
            onSuccess: () => {
                refetch()
                reset()
                onClose()
            },
        }
    )

    const onSubmit = async (data: FormValues) => {
        await mutateAsync({
            department_name: data.department_name,
            department_code: data.department_code,
            department_description: data.department_description,
            department_status: data.department_status,
            department_leader_id: data.department_leader_id || undefined,
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('create_department')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-4 space-y-4">
                    <FormItem
                        control={control}
                        name="department_name"
                        type="textfield"
                        label={t('department_name')}
                        placeholder={t('enter_department_name')}
                    />

                    <FormItem
                        control={control}
                        name="department_code"
                        type="textfield"
                        label={t('department_code')}
                        placeholder={t('enter_department_code')}
                    />

                    <EmployeeSelect
                        control={control}
                        name="department_leader_id"
                        label="department_leader"
                        onlyActive
                    />

                    <FormItem
                        control={control}
                        name="department_description"
                        type="textarea"
                        label={t('department_description')}
                        placeholder={t('enter_department_description')}
                    />

                    <FormItem
                        control={control}
                        name="department_status"
                        type="select"
                        label={t('status')}
                        options={DEPARTMENT_STATUS(t)}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            type="button"
                        >
                            {t('cancel')}
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            loading={isPending}
                        >
                            {t('create')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default DepartmentCreateModal
